import React from 'react';
import { Controller, FieldErrors, useFormContext } from 'react-hook-form';
import { TextField } from '@mui/material';

interface TextAreaFormControlProps {
    name: string;
    label: string;
    required?: boolean;
    style?: React.CSSProperties;
    rows?: number;
    maxLength?: number;
    validateConditionally?: (value: string, formValues: any) => true | string;
}

export const TextAreaFormControl: React.FC<TextAreaFormControlProps> = ({ name, label, required = false, style, rows = 4, maxLength, validateConditionally, ...otherProps }) => {
    const { control, formState: { errors }, clearErrors, getValues } = useFormContext();

    const getNestedError = (name: string): FieldErrors | undefined => {
        return name.split('.').reduce((acc: any, curr: string) => {
            return acc && acc[curr] ? acc[curr] : undefined;
        }, errors);
    };

    const validateNotOnlySpaces = (value: string) => {
        return value.trim().length > 0 || 'Поле обязательно для заполнения';
    };

    const fieldError = getNestedError(name);
    const isError = Boolean(fieldError);

    const rules = React.useMemo(() => {
        const validateFn = validateConditionally 
            ? (value: string) => {
                const formValues = getValues();
                return validateConditionally(value, formValues);
            }
            : (required ? validateNotOnlySpaces : undefined);

        return {
            required: required && !validateConditionally ? 'Поле обязательно для заполнения' : false,
            ...(validateFn && { validate: validateFn }),
            maxLength: maxLength ? { value: maxLength, message: `Максимальная длина ${maxLength} символов` } : undefined,
        };
    }, [required, maxLength, validateConditionally, getValues]);

    // Clear errors when required changes from true to false (for non-conditional validation)
    const prevRequiredRef = React.useRef(required);
    React.useEffect(() => {
        if (prevRequiredRef.current && !required && !validateConditionally) {
            // Required changed from true to false - clear errors
            clearErrors(name);
        }
        prevRequiredRef.current = required;
    }, [required, name, clearErrors, validateConditionally]);

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => (
                <TextField
                    {...field}
                    label={label}
                    multiline
                    rows={rows}
                    error={isError}
                    helperText={isError ? (fieldError as any)?.message : ''}
                    required={required}
                    style={style}
                    fullWidth
                    {...otherProps}
                />
            )}
        />
    );
};
